import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';
import { Button } from '../../components/v3';
import {
  Form,
  Formik
} from 'formik';
import React, {
  useEffect,
  useState,
  useRef
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useRecoilState,
  useResetRecoilState,
  useSetRecoilState,
  useRecoilValue
} from 'recoil';
import * as yup from 'yup';
import { StaticRoutes } from '../../Router';
import {
  OtpEntryModal,
  SupportPopup,
  Footer,
  Header
} from '../../components';
import { useModal } from '../../components/modalController/useModal';
import {
  ApplicationSteps,
  FormContainer,
  emailRegex,
  msisdnRegex,
  namesRegex
} from '../../constants';
import { ValidationErrorMessageMap } from '../../constants/validationErrorMessageMap';
import { findFirstValidationError } from '../../helpers/formik';
import {
  authDetail,
  continueClickedState,
  requestOtpRegister,
  requestRegisterOtpState,
  verifyRegisterOtp,
  verifyRegisterOtpState,
  userDetails
} from '../../recoil';
import { useTealium } from '../../tealium/useTealium';
import { setTealiumUser } from '../../tealium/tealiumUser';
import styles from './Registration.module.css';
import { formInitialValues, formFields } from './RegistrationConstants';
import { FormBuilder } from '../../components/FormBuilder';
const privacyCheckBoxError = 'Privacy consent is required';

const validationSchema = yup.object({
  firstName: yup.string().trim().matches(namesRegex, 'Name must contain letters only').min(1, 'Name should be of minimum 1 character length').max(40, 'Name should be of maximum 40 characters length').required('Name is a required field'),
  lastName: yup.string().trim().matches(namesRegex, 'Surname must contain letters only').min(1, 'Surname should be of minimum 1 character length').max(40, 'Surname should be of maximum 40 characters length').required('Surname is a required field'),
  email: yup.string().matches(emailRegex, 'Email address must be a valid email address').required('Email address is a required field'),
  msisdn: yup.string().matches(msisdnRegex, 'Mobile number must be a valid South African mobile number').required('Mobile number is a required field'),
  privacyConsent: yup.boolean().oneOf([true], privacyCheckBoxError).required(privacyCheckBoxError)
});

const wizardSteps = Object.values(ApplicationSteps).map((s) => ({
  label: s.title,
  subSteps: 0
}));

export const Registration = () => {

  const otpPending = useRef(false);
  const [otpResponse, setOtpRequest] = useRecoilState(requestOtpRegister);
  const [verifyOtpResponse, setOtpVerification] = useRecoilState(verifyRegisterOtp);
  const [getAuthDetail, setAuthDetail] = useRecoilState(authDetail);
  const navigate = useNavigate();
  const location = useLocation();
  const [continueClicked, setContinueClicked] = useRecoilState(continueClickedState);
  const [openModal, closeModal] = useModal();
  const resetOtpRequestState = useResetRecoilState(requestRegisterOtpState);
  const resetOtpResponseState = useResetRecoilState(verifyRegisterOtpState);
  const resetLogin = useSetRecoilState(authDetail);
  const { track, events } = useTealium();
  let _userInfo = useRecoilValue(userDetails);
  const [formData, setFormData] = useState(formInitialValues);
  const hasFormStarted = useRef(false);

  // let formTouched = false;

  // const handleFormFocus = () => {
  //   if (formTouched) {
  //     return;
  //   }

  //   formTouched = true;
  //   track(events.registration.formStart());
  // };

  useEffect(() => {
    track(events.registration.pageView());
  }, []);

  useEffect(() => {
    if (verifyOtpResponse?.token) {
      setAuthDetail(verifyOtpResponse);
    }
  }, [verifyOtpResponse, setAuthDetail]);

  useEffect(() => {
    if (getAuthDetail?.token) {
      // navigate is still not working
      // I just wasted 3 days trying to fix this. Defer investigation until later.
      // I suspect that the issue is that one of the atoms in login.js retains an error
      // state, even after logging in, so when the login is completed the console is
      // flooded with VlUnauthorized errors. Setting the window location as below
      // forces the browser to refresh, and all atoms are refreshed.
      navigate(StaticRoutes.FundingApplicationDetails);
      closeOtpModal();
    }
  }, [getAuthDetail]);

  const sendOtpRequest = (userDetailsInput) => {
    setTealiumUser(userDetailsInput);

    if (!otpPending.current) {

      //   otpPending.current = true;
      setOtpRequest(userDetailsInput);
    }
  };

  useEffect(() => {
    if (otpResponse) {
      otpPending.current = false;
    }
  }, [otpResponse]);

  const DetailsEntry = () => {

    const formRef = useRef();

    const handleSubmit = (formValues) => {

      setFormData(formValues);
      _userInfo = formData;
      if (formRef.current) {
        track(events.registration.getStarted(formData?.privacyConsent));
        formRef.current.handleSubmit();
      }
      setContinueClicked(true);
      sendOtpRequest({
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        msisdn: formValues.msisdn,
        journeyType: 'register',
        privacyConsent: typeof formValues.privacyConsent === 'undefined' ? false : formValues.privacyConsent
      });
    };

    const lastErrorRef = useRef(null);

    const handleFormikTouchedErrors = (touched, errors) => {
      const errorMessage = findFirstValidationError(errors, touched);
      if (!errorMessage || lastErrorRef.current === errorMessage) {
        return;
      }
      track(events.error('form', location.pathname, errorMessage));
      lastErrorRef.current = errorMessage;
    };

    const handleFirstInteraction = (e) => {
      if (hasFormStarted.current) { return; }
      const el = e.target.closest('input, select, textarea');
      if (!el) { return; }
      hasFormStarted.current = true;
      track(events.registration.formStart());
    };

    const handleCheckboxClick = (e) => {
      const el = e.target.closest('input[type="checkbox"]');
      if (!el) { return; }
      track(events.registration.checkBox());
    };

    const handleFormClick = (e) => {
      handleFirstInteraction(e); // fires formStart
      handleCheckboxClick(e); // fires checkbox analytics
    };

    return (
      <div>
        <ThemeProvider theme={THEME.revamp} >
          <Formik
            initialValues={formData}
            onSubmit={handleSubmit}
            validate={(values) => {
              try {
                validationSchema.validateSync(values, { abortEarly: false });
                return {};
              } catch (error) {
                return error.inner.reduce((acc, err) => {
                  acc[err.path] = err.message;
                  return acc;
                }, {});
              }
            }}
            validateOnBlur={true}
            validateOnMount={true}
            innerRef={formRef}
          >
            {({ values, errors, touched }) => {
              handleFormikTouchedErrors(touched, errors);
              return (
                <div>
                  <Form style={FormContainer}
                    onClickCapture={handleFormClick}
                    onChangeCapture={handleFirstInteraction}
                  >
                    <FormBuilder
                      fields={formFields}
                      title="Start your funding application "
                      subtitle="Please give us your details"
                      values={values}
                      errors={errors}
                      touched={touched}
                      requireFooter={false}
                    />
                    <Footer
                      rightTitle={'Get started'}
                      rightCallback={Object.keys(errors).length !== 0 || !values.privacyConsent ? null : handleSubmit}
                    />
                  </Form>
                </div>);
            }}
          </Formik>
        </ThemeProvider>
        <SupportPopup />
      </div>
    );

  };

  const closeOtpModal = () => {
    closeModal();
    resetOtpResponseState();
    resetOtpRequestState();
    setContinueClicked(false);
  };

  const resetAllResponse = () => {
    resetOtpResponseState();
    resetOtpRequestState();
  };

  const submitOtp = (otp) => {
    track(events.otp.continue());
    setOtpVerification(otp);
  };

  const resendOtp = () => {
    track(events.otp.resend());
    setOtpRequest(null);
  };

  useEffect(() => {

    const errorMsg = ValidationErrorMessageMap[verifyOtpResponse?.message] ||
      ValidationErrorMessageMap[otpResponse?.message] || otpResponse?.error?.message || verifyOtpResponse?.error?.message;
    if (errorMsg) {

      if (errorMsg === ValidationErrorMessageMap.InvalidLoginRef) {
        track(events.error('form', location.pathname, errorMsg));
        resetLogin(null);
        openModal({
          title: 'Error',
          children: <div dangerouslySetInnerHTML={{ __html: 'Please Try again' }} />,
          type: 'error',
          onCloseModal: closeOtpModal,
          actions: [<Button key="close" onClick={closeOtpModal}>Close</Button>]
        });
        return;
      }

      if (errorMsg === 'The request was sent but no response was received') {
        track(events.error('form', location.pathname, errorMsg));

        openModal({
          title: 'Error',
          children: <div dangerouslySetInnerHTML={{ __html: ValidationErrorMessageMap[errorMsg] }} />,
          type: 'error',
          onCloseModal: closeOtpModal,
          actions: [<Button key="close" onClick={closeOtpModal}>Try again</Button>]
        });
        return;
      }

      track(events.error('event', location.pathname, errorMsg));
      openModal({
        title: 'Error',
        children: <div dangerouslySetInnerHTML={{ __html: errorMsg }} />,
        type: 'error',
        onCloseModal: closeOtpModal,
        actions: [<Button key="close" onClick={closeOtpModal}>Close</Button>]
      });
      return;
    }

    if (continueClicked) {
      openModal({
        children: <OtpEntryModal
          maskedContact={otpResponse?.maskedContact}
          verifyOtpResponse={verifyOtpResponse}
          otpResponse={otpResponse}
          submitOtp={submitOtp}
          resendOtp={resendOtp}
          resetAllResponse={resetAllResponse}
          onView={() => track(events.otp.pageView())}
          closeOtpModal={closeOtpModal}
        />,
        onCloseModal: closeOtpModal
      });
    }
  }, [otpResponse, continueClicked, verifyOtpResponse]);

  return (
    <div className={styles.loginPage}>
      <Header
        currentStep={1}
        activeSubStep={0}
        steps={wizardSteps}
      />
      <DetailsEntry />
    </div>
  );
};
